/**
 * Review workflows for the admin queues (ADMIN-01 onboarding, ADMIN-02
 * moderation, ADMIN-03 exceptions). Each action is a declaration: which
 * statuses it may leave, the status it enters, whether a reason is mandatory,
 * and an optional guard. Pure: returns a new item with a decision-trail entry.
 *
 * The server enforces the same rules (lib/platform/workflows.js); this copy
 * only decides which buttons to show.
 */

const allDocsVerified = (item) => (item.documents || []).every((d) => d.status === 'verified');
const bankVerified = (item) => !!(item.bank && item.bank.status === 'verified');

export const WORKFLOWS = {
  onboarding: {
    start_review: { label: 'Start review', variant: 'outline', from: ['submitted'], to: 'in_review', reasonRequired: false },
    approve: {
      label: 'Approve', variant: 'success', from: ['submitted', 'in_review', 'info_requested'], to: 'approved', reasonRequired: false,
      guard: (item) => (allDocsVerified(item) && bankVerified(item)
        ? null
        : { error: 'EVIDENCE_INCOMPLETE', message: 'Every KYC document and the bank account must be verified before approval.' }),
    },
    request_info: { label: 'Request info', variant: 'outline', from: ['submitted', 'in_review'], to: 'info_requested', reasonRequired: true },
    reject: { label: 'Reject', variant: 'destructive', from: ['submitted', 'in_review', 'info_requested'], to: 'rejected', reasonRequired: true },
    pause: { label: 'Pause seller', variant: 'warning', from: ['approved'], to: 'paused', reasonRequired: true },
    restore: { label: 'Restore seller', variant: 'success', from: ['paused'], to: 'approved', reasonRequired: true },
  },
  moderation: {
    approve: {
      label: 'Approve & publish', variant: 'success', from: ['pending_review', 'flagged'], to: 'approved', reasonRequired: false,
      // A listing with a blocking flag can be published only with a written override.
      guard: (item, { reason }) => ((item.flags || []).some((f) => f.severity === 'high') && !String(reason || '').trim()
        ? { error: 'REASON_REQUIRED', message: 'This listing has a high-severity flag; give a reason to override it.' }
        : null),
    },
    reject: { label: 'Reject', variant: 'destructive', from: ['pending_review', 'flagged', 'approved'], to: 'rejected', reasonRequired: true },
  },
  exceptions: {
    assign: {
      label: 'Assign', variant: 'outline', from: ['open', 'assigned'], to: 'assigned', reasonRequired: false,
      guard: (item, { assignee }) => (assignee ? null : { error: 'ASSIGNEE_REQUIRED', message: 'Choose who owns this exception.' }),
      apply: (item, { assignee }) => ({ assignee }),
    },
    resolve: { label: 'Resolve', variant: 'success', from: ['open', 'assigned'], to: 'resolved', reasonRequired: true },
  },
};

/** Actions available for an item in its current status. */
export function availableActions(workflow, item) {
  const defs = WORKFLOWS[workflow] || {};
  return Object.entries(defs)
    .filter(([, d]) => d.from.includes(item.status))
    .map(([key, d]) => ({ key, ...d }));
}

/**
 * Apply one decision.
 * @returns {{ ok: true, item } | { ok: false, status, error, message }}
 */
export function applyDecision(workflow, item, action, ctx = {}) {
  const def = (WORKFLOWS[workflow] || {})[action];
  if (!def) return { ok: false, status: 400, error: 'UNKNOWN_ACTION', message: `"${action}" is not a ${workflow} action.` };
  if (!def.from.includes(item.status)) {
    return { ok: false, status: 409, error: 'INVALID_TRANSITION', message: `Cannot ${def.label.toLowerCase()} an item that is ${String(item.status).replace(/_/g, ' ')}.` };
  }
  const reason = String(ctx.reason || '').trim();
  if (def.reasonRequired && !reason) return { ok: false, status: 400, error: 'REASON_REQUIRED', message: `${def.label} needs a reason.` };
  const blocked = def.guard ? def.guard(item, { ...ctx, reason }) : null;
  if (blocked) return { ok: false, status: 409, ...blocked };
  const entry = { at: ctx.at || new Date().toISOString(), actor: ctx.actor || 'superadmin', action, from: item.status, to: def.to, reason: reason || null };
  return {
    ok: true,
    item: { ...item, ...(def.apply ? def.apply(item, ctx) : {}), status: def.to, updated_at: entry.at, trail: [...(item.trail || []), entry] },
  };
}

/** Readiness of a seller for payouts: bank verified + GST/PAN verified + agreement signed. */
export function payoutReadiness(item) {
  const docs = Object.fromEntries((item.documents || []).map((d) => [d.type, d.status]));
  const checks = [
    { key: 'bank', label: 'Bank account verified (penny drop)', ok: bankVerified(item) },
    { key: 'pan', label: 'PAN verified', ok: docs.pan === 'verified' },
    { key: 'gst', label: 'GST certificate verified', ok: docs.gst_certificate === 'verified' },
    { key: 'agreement', label: 'Seller agreement signed', ok: !!item.agreement_signed },
  ];
  return { ready: checks.every((c) => c.ok), checks };
}
